import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_CONTACT_SETS, Account, AccountContactSet, withStyles } from 'vopay';
import { BodyText, Button } from 'UI';
import { useBlockingLoader } from 'shared';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useToast } from '@/providers/Toast.provider';
import useUpdateAccountContacts from '@/components/Entities/hooks/useApi/useUpdateAccountContacts';
import ContactCard, { type Contact, type ContactRole } from '@/components/Entities/UI/ContactCard/ContactCard';
import useContactsFormConfig, {
  type ContactsFormValues,
  toChangedContacts,
  toContactsFormValues,
} from './useContactsFormConfig';
import styles from './ContactInformation.module.scss';

const cn = withStyles(styles);

const ROLES: Record<AccountContactSet, ContactRole> = {
  PrimaryContact: 'primary',
  BillingContact: 'billing',
  TechnicalContact: 'technical',
  SupportContact: 'support',
};

type ContactInformationProps = {
  /** Undefined while the account loads, null when the request failed. */
  account: Account | null | undefined;
  /** The account's own bearer, which is what the contact update route is signed with. */
  accountToken: string;
  /** Reloads the account after a save, so the cards show what the backend kept. */
  onContactsUpdated: () => Promise<unknown>;
};

/**
 * The Details tab's Contact Information section (spec 5.6.1, ID-13964 D1): the four contact cards,
 * with an Edit button that turns every value into an input, and Cancel / Save in its place while
 * editing. Save is enabled once something changed and everything entered is valid.
 */
export default function ContactInformation({ account, accountToken, onContactsUpdated }: ContactInformationProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.DetailsTab.Contacts' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const toast = useToast();
  const blockingLoader = useBlockingLoader();
  const { updateAccountContacts } = useUpdateAccountContacts();
  const [isEditing, setIsEditing] = useState(false);

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  // Figma "Edit Primary Contact": a card's heading while its fields are inputs.
  const editTitles: Record<ContactRole, string> = {
    primary: t('editPrimary', { defaultValue: 'Edit Primary Contact' }),
    billing: t('editBilling', { defaultValue: 'Edit Billing Contact' }),
    technical: t('editTechnical', { defaultValue: 'Edit Technical Contact' }),
    support: t('editSupport', { defaultValue: 'Edit Support Contact' }),
  };

  const initialValues = useMemo(() => toContactsFormValues(account), [account]);

  const handleSave = async (values: ContactsFormValues) => {
    const changes = toChangedContacts(values, initialValues);

    // A change that is only spaces leaves the form dirty, so Save is enabled, but trims back to what is
    // saved: there is nothing to send.
    if (Object.keys(changes).length === 0) {
      setIsEditing(false);

      return;
    }

    // Blocking while the save is out: the cards below are what is being written, so nothing on the
    // page is safe to touch until the backend has answered.
    blockingLoader.show();

    try {
      await updateAccountContacts(changes, accountToken);
    } catch (error) {
      // The hook has logged the cause; the form stays open with what was typed.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('saveError', { defaultValue: 'Could not save the contact information: {{errorMessage}}', errorMessage }),
      );

      return;
    } finally {
      blockingLoader.hide();
    }

    setIsEditing(false);
    toast.success(t('updated', { defaultValue: 'Contact Information Updated' }));

    // The page reports a failed reload itself; the save has already gone through.
    onContactsUpdated().catch(() => {});
  };

  const { ctx, renderContactFields } = useContactsFormConfig({ initialValues, onSubmit: handleSave });

  const handleCancel = () => {
    ctx.resetForm();
    setIsEditing(false);
  };

  // `GET /partner/account` returns no occupation, so the cards render without that row. A contact the
  // response leaves out reads as unset, like one sent with every field empty.
  const contact = (contactSet: AccountContactSet): Contact | null | undefined => {
    if (account === undefined) return undefined;
    if (account === null) return null;

    const current = account[contactSet];

    return {
      fullName: current?.FullName ?? '',
      title: current?.Title ?? '',
      email: current?.Email ?? '',
      phoneNumber: current?.PhoneNumber ?? '',
    };
  };

  return (
    <form
      className={cn('contacts')}
      aria-label={t('title', { defaultValue: 'Contact Information' })}
      data-testid="accountContacts"
      onSubmit={ctx.handleSubmit}
      noValidate
    >
      <div className={cn('heading')}>
        {/* Figma: Body/13/Medium in Text/Secondary75 */}
        <BodyText size="sm" weight="md" className={cn('contactsTitle')}>
          {t('title', { defaultValue: 'Contact Information' })}
        </BodyText>

        {/* Figma "Buttons": Type="Secondary"; Size="Small"; Left Indicator="edit" / Cancel + Primary Save */}
        {isEditing ? (
          <div className={cn('actions')}>
            <Button type="button" size="small" onClick={handleCancel} disabled={ctx.isSubmitting}>
              {tGlobal('Buttons.cancel')}
            </Button>

            <Button
              type="submit"
              colorScheme="primary"
              size="small"
              disabled={!ctx.dirty || !ctx.isValid || ctx.isSubmitting}
            >
              {tGlobal('Buttons.save')}
            </Button>
          </div>
        ) : (
          // Nothing to edit until the account has loaded, and a view-only role never may.
          !isRoleViewOnly && (
            <Button type="button" size="small" iconLeft="edit" disabled={!account} onClick={() => setIsEditing(true)}>
              {tGlobal('Buttons.edit')}
            </Button>
          )
        )}
      </div>

      {ACCOUNT_CONTACT_SETS.map((contactSet) => (
        <ContactCard
          key={contactSet}
          role={ROLES[contactSet]}
          size="sm"
          contact={contact(contactSet)}
          edit={
            isEditing ? { title: editTitles[ROLES[contactSet]], fields: renderContactFields(contactSet) } : undefined
          }
        />
      ))}
    </form>
  );
}
